// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import { logger } from '../../lib/logger';
import { useGameStore } from '../../store/gameStore';

interface Persona {
  id: string;
  name: string;
  description: string;
}

export function NpcManager() {
  const sessionId = useGameStore(state => state.sessionId);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [selectedPersonas, setSelectedPersonas] = useState<string[]>([]);
  const [chatEnabledPersonas, setChatEnabledPersonas] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPersonas = async () => {
      try {
        setIsLoading(true);
        const fetchedPersonas = await api.listNpcPersonas();
        setPersonas(fetchedPersonas as unknown as Persona[]);
        setError(null);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load NPC personas';
        logger.error('npc_persona_fetch_failed', { error: errorMessage });
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPersonas();
  }, []);

  const handleTogglePersona = (personaId: string) => {
    setSelectedPersonas(prev =>
      prev.includes(personaId) ? prev.filter(id => id !== personaId) : [...prev, personaId]
    );
  };

  const handleToggleChat = useCallback((personaId: string) => {
    setChatEnabledPersonas(prev => {
      const newSet = new Set(prev);
      if (newSet.has(personaId)) {
        newSet.delete(personaId);
      } else {
        newSet.add(personaId);
      }
      return newSet;
    });
  }, []);

  const handleAddNpcs = async () => {
    logger.debug('handleAddNpcs called');
    if (!sessionId || selectedPersonas.length === 0) {
      logger.warn('handleAddNpcs skipped', {
        hasSessionId: !!sessionId,
        selectedCount: selectedPersonas.length,
      });
      return;
    }

    const payload = selectedPersonas.map(id => ({
      persona_id: id,
      allow_chat: chatEnabledPersonas.has(id),
    }));

    logger.info('Attempting to add NPCs', { sessionId, payload });
    try {
      setIsAdding(true);
      setError(null);
      const response = await api.addNpcsToRoom(sessionId, payload);
      logger.info('addNpcsToRoom API call successful', { response });

      // WORKAROUND: Cloudflare WebSocket Hibernation doesn't deliver broadcasts from Python DOs
      // Fetch fresh state via REST API and update store directly
      logger.info('Fetching fresh session state after NPC addition');
      const freshStateRaw = await api.getRoomDetails(sessionId);
      const freshState = freshStateRaw as {
        players?: string[];
        player_info?: Record<string, unknown>;
        [key: string]: unknown;
      };
      logger.info('Fresh state fetched', {
        playerCount: freshState.players?.length,
      });

      // Build current_players array from player_info (needed for Lobby component)
      const currentPlayers =
        freshState.players
          ?.map((playerId: string) => freshState.player_info?.[playerId])
          .filter(Boolean) || [];

      // Update game store with fresh state including current_players
      const gameStore = useGameStore.getState();
      gameStore.updateGameState({
        ...freshState,
        current_players: currentPlayers,
      } as Parameters<typeof gameStore.updateGameState>[0]);

      // Reset selection
      setSelectedPersonas([]);
      setChatEnabledPersonas(new Set());
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add NPCs';
      logger.error('npc_add_failed', { error: errorMessage });
      setError(errorMessage);
    } finally {
      setIsAdding(false);
    }
  };

  if (isLoading) {
    return <div className="text-sm text-gray-400">Loading NPC personas...</div>;
  }

  if (error) {
    return <div className="text-sm text-red-400">Error: {error}</div>;
  }

  return (
    <div className="mt-6 p-4 border border-gray-700 rounded-lg bg-gray-800/50">
      <h3 className="font-semibold text-lg mb-4">Add AI Players</h3>
      <div className="space-y-3">
        {personas.map(persona => (
          <div key={persona.id} className="p-2 rounded-md hover:bg-gray-700/50">
            <div className="flex items-start">
              <input
                type="checkbox"
                id={`persona-${persona.id}`}
                checked={selectedPersonas.includes(persona.id)}
                onChange={() => handleTogglePersona(persona.id)}
                className="mt-1 h-4 w-4 rounded border-gray-600 bg-gray-700 text-indigo-500 focus:ring-indigo-500"
              />
              <label htmlFor={`persona-${persona.id}`} className="ml-3 text-sm flex-grow">
                <span className="font-bold text-white">{persona.name}</span>
                <p className="text-gray-400">{persona.description}</p>
              </label>
            </div>
            {selectedPersonas.includes(persona.id) && (
              <div className="pl-7 mt-2 flex items-center">
                <input
                  type="checkbox"
                  id={`persona-chat-${persona.id}`}
                  checked={chatEnabledPersonas.has(persona.id)}
                  onChange={() => handleToggleChat(persona.id)}
                  className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-indigo-500 focus:ring-indigo-500"
                />
                <label
                  htmlFor={`persona-chat-${persona.id}`}
                  className="ml-2 text-xs text-gray-300"
                >
                  Allow this NPC to chat
                </label>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-4">
        <button
          onClick={handleAddNpcs}
          disabled={
            isAdding || !sessionId || selectedPersonas.length === 0 || selectedPersonas.length > 3
          }
          className="w-full px-4 py-2 bg-indigo-600 text-white font-semibold rounded-md hover:bg-indigo-500 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
        >
          {isAdding ? 'Adding NPCs...' : `Add ${selectedPersonas.length} NPC(s)`}
        </button>
        {selectedPersonas.length > 3 && (
          <p className="text-red-400 text-sm mt-2">You can add a maximum of 3 NPCs.</p>
        )}
      </div>
    </div>
  );
}
